import {
	type CompanyContactId,
	getAllowedIndemnitySignatureMethods,
	getCompanySigningStates,
	type IndemnityAgreementSignatureMethod,
	type IndividualContactId,
} from "@inrev/common";
import { formatName } from "../../../utils";
import type { AdminAccountCompany, AdminAccountIndividual } from "./utils";

type AdminAccountIndemnitorGroup = {
	companies: { id: CompanyContactId; name: string }[];
	individuals: { id: IndividualContactId; name: string; email?: string }[];
};

export type AdminAccountIndemnitySummary = {
	allowedSignatureMethods: IndemnityAgreementSignatureMethod[];
	requiredIndemnitors: AdminAccountIndemnitorGroup;
	indemnityWaivers: AdminAccountIndemnitorGroup;
};

const individualDisplayName = (individual: AdminAccountIndividual): string =>
	formatName(individual) ?? "";

export const deriveAdminAccountIndemnitySummary = (
	companies: AdminAccountCompany[],
	individuals: AdminAccountIndividual[],
): AdminAccountIndemnitySummary => {
	const requiredCompanyIndemnitors = companies.filter(
		(company) => company.config.contract.includeInIndemnity,
	);
	const requiredIndividualIndemnitors = individuals.filter(
		(individual) => individual.config?.contract.includeInIndemnity,
	);
	const waivedCompanies = companies.filter(
		(company) => !company.config.contract.includeInIndemnity,
	);
	const waivedIndividuals = individuals.filter(
		(individual) => !individual.config?.contract.includeInIndemnity,
	);

	return {
		allowedSignatureMethods: getAllowedIndemnitySignatureMethods(
			getCompanySigningStates(requiredCompanyIndemnitors),
		),
		requiredIndemnitors: {
			companies: requiredCompanyIndemnitors.map((company) => ({
				id: company.id,
				name: company.name,
			})),
			individuals: requiredIndividualIndemnitors.map((individual) => ({
				id: individual.id,
				name: individualDisplayName(individual),
				email: individual.email,
			})),
		},
		indemnityWaivers: {
			companies: waivedCompanies.map((company) => ({
				id: company.id,
				name: company.name,
			})),
			individuals: waivedIndividuals.map((individual) => ({
				id: individual.id,
				name: individualDisplayName(individual),
				email: individual.email,
			})),
		},
	};
};
